import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import { isTokenExpired } from '@/services/apiClient';
import { AuthState, AuthResponse } from '@/types/auth';

// Remove the local interface since it's now imported from types

// Check for existing token in localStorage with expiration
const getInitialAuthState = (): AuthState => {
  const token = localStorage.getItem('finara_token');
  const userId = localStorage.getItem('finara_user_id');
  const expiresAtString = localStorage.getItem('finara_token_expires');

  if (token && userId && expiresAtString) {
    const expiresAt = parseInt(expiresAtString);

    // Check if token is expired
    if (isTokenExpired(expiresAt)) {
      localStorage.removeItem('finara_token');
      localStorage.removeItem('finara_user_id');
      localStorage.removeItem('finara_token_expires');

      return {
        user: null,
        token: null,
        expiresAt: null,
        userContext: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    }

    return {
      user: null,
      token,
      expiresAt,
      userContext: null,
      isAuthenticated: true,
      isLoading: true,
      error: null,
    };
  }

  return {
    user: null,
    token: null,
    expiresAt: null,
    userContext: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
  };
};

const initialState: AuthState = getInitialAuthState();

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: state => {
      state.isLoading = true;
      state.error = null;
    },
    loginSuccess: (state, action: PayloadAction<AuthResponse>) => {
      state.isLoading = false;
      state.isAuthenticated = true;
      state.user = action.payload.user;
      state.token = action.payload.token;
      state.expiresAt = action.payload.expiresAt;
      state.userContext = action.payload.userContext;
      state.error = null;

      localStorage.setItem('finara_token', action.payload.token);
      localStorage.setItem('finara_user_id', action.payload.user.id);
      localStorage.setItem('finara_token_expires', action.payload.expiresAt.toString());
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.expiresAt = null;
      state.userContext = null;
      state.error = action.payload;

      localStorage.removeItem('finara_token');
      localStorage.removeItem('finara_user_id');
      localStorage.removeItem('finara_token_expires');
    },
    logout: state => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.expiresAt = null;
      state.userContext = null;
      state.error = null;

      // Clear localStorage
      localStorage.removeItem('finara_token');
      localStorage.removeItem('finara_user_id');
      localStorage.removeItem('finara_token_expires');
    },
    clearError: state => {
      state.error = null;
    },
    setUserData: (
      state,
      action: PayloadAction<{ user: AuthState['user']; userContext: AuthState['userContext'] }>
    ) => {
      state.user = action.payload.user;
      state.userContext = action.payload.userContext;
      state.isLoading = false;
    },
    tokenExpired: state => {
      state.isAuthenticated = false;
      state.user = null;
      state.token = null;
      state.expiresAt = null;
      state.userContext = null;
      state.error = 'Sesión expirada. Inicia sesión nuevamente.';

      localStorage.removeItem('finara_token');
      localStorage.removeItem('finara_user_id');
      localStorage.removeItem('finara_token_expires');
    },
  },
});

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  clearError,
  setUserData,
  tokenExpired,
} = authSlice.actions;
export default authSlice.reducer;
